import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { signInWithEmailAndPassword, signOut, sendPasswordResetEmail } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { projectAuth, projectFunctions } from '../firebase/config';
import { checkIsAdmin } from '../constants/admin';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(undefined);
  const [isAdmin, setIsAdmin] = useState(null); // null = loading, true/false once resolved
  const [isPremium, setIsPremium] = useState(null);
  const [emailIsVerified, setEmailIsVerified] = useState(null);
  const [loading, setLoading] = useState(true);

  const resolveAdminStatus = async (user) => {
    if (!user) {
      return false;
    }
    try {
      const idTokenResult = await user.getIdTokenResult();
      return checkIsAdmin(user, idTokenResult);
    } catch (error) {
      console.error("Error checking admin status:", error);
      return false;
    }
  };

  // Local Firebase sign-out only — never touch yalango.com session cookies
  const localSignOut = useCallback(async () => {
    await signOut(projectAuth);
    setCurrentUser(null);
    setIsAdmin(false);
    setIsPremium(null);
    setEmailIsVerified(null);
  }, []);

  const login = useCallback(async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(projectAuth, email, password);
    const user = userCredential.user;
    const admin = await resolveAdminStatus(user);

    if (!admin) {
      // Sign out locally; do not set isAdmin true at any point for this user
      await signOut(projectAuth);
      setCurrentUser(null);
      setIsAdmin(false);
      setIsPremium(null);
      setEmailIsVerified(null);
      const unauthorizedError = new Error("Not authorized");
      unauthorizedError.code = "auth/not-authorized";
      throw unauthorizedError;
    }

    // Set user + admin together so the guard never sees isAdmin without currentUser
    setCurrentUser(user);
    setIsAdmin(true);
    return { user, isAdmin: true };
  }, []);

  const checkIfPremiumUser = async (user = currentUser) => {
    if (!user) {
      setIsPremium(false);
      return false;
    }
    try {
      const idTokenResult = await user.getIdTokenResult();
      const premium = idTokenResult.claims?.stripeRole === "premium";
      setIsPremium(premium);
      return premium;
    } catch (error) {
      console.error("Error checking premium status:", error);
      return false;
    }
  };

  const checkIfVerifiedEmail = async (user = currentUser) => {
    if (!user) {
      setEmailIsVerified(false);
      return false;
    }

    try {
      await user.reload();
      const verified = user.emailVerified;
      setEmailIsVerified(verified);
      return verified;
    } catch (error) {
      console.error("Error checking email verification:", error);
      setEmailIsVerified(false);
      return false;
    }
  };

  const refreshEmailVerification = async () => {
    await checkIfVerifiedEmail();
  };

  const verifyEmail = async (user = currentUser) => {
    if (!user) {
      throw new Error("No user found");
    }

    try {
      const sendVerifyEmailLinkApi = httpsCallable(projectFunctions, "sendVerifyEmailLinkApi");
      await sendVerifyEmailLinkApi({ uid: user.uid });
      return { success: true };
    } catch (error) {
      console.error("Error sending verification email:", error);
      throw error;
    }
  };

  // Dashboard logout: local Firebase only (does not log out of yalango.com)
  const logout = useCallback(async () => {
    try {
      await localSignOut();
    } catch (error) {
      console.error("Error logging out:", error);
    }
  }, [localSignOut]);

  const resetPassword = useCallback(async (email) => {
    await sendPasswordResetEmail(projectAuth, email);
  }, []);

  useEffect(() => {
    const unsubscribe = projectAuth.onAuthStateChanged(async (user) => {
      if (!user) {
        setCurrentUser(null);
        setIsAdmin(false);
        setIsPremium(null);
        setEmailIsVerified(null);
        setLoading(false);
        return;
      }

      const admin = await resolveAdminStatus(user);
      if (!admin) {
        // Clear any non-admin Firebase session on this origin only
        try {
          await signOut(projectAuth);
        } catch (error) {
          console.error("Error signing out non-admin:", error);
        }
        setCurrentUser(null);
        setIsAdmin(false);
        setIsPremium(null);
        setEmailIsVerified(null);
        setLoading(false);
        return;
      }

      setCurrentUser(user);
      setIsAdmin(true);
      await checkIfPremiumUser(user);
      await checkIfVerifiedEmail(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const authReady = currentUser !== undefined && isAdmin !== null;

  const value = {
    currentUser,
    isAdmin,
    isPremium,
    emailIsVerified,
    authReady,
    logout,
    login,
    resetPassword,
    checkIfPremiumUser,
    checkIfVerifiedEmail,
    refreshEmailVerification,
    verifyEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
